import { FC, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import useToggle from '@hooks/useToggle';
import { IAdminOrganizationFormValues } from '@interfaces/adminOrganizations';
import {
  handleAdminActivateOrganization,
  handleAdminDeactivateOrganization,
  handleAdminEditOrganization,
  handleGetAdminOrganizationDetails,
} from '@store/features/adminOrganizations/actions';
import { adminOrganizationActivatingOrDeactivating } from '@store/features/adminOrganizations/selectors';
import { removeAdminOrganizationDetails } from '@store/features/adminOrganizations/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import OrganizationDetailsForm from '../OrganizationDetailsForm/OrganizationDetailsForm';

import { IOrganizationDetailsProps } from './OrganizationDetails.interface';
import {
  convertAdminOrganizationDetailsIntoFormValues,
  convertFormValuesToEditPayload,
  validateForm,
} from './utils';

const OrganizationDetails: FC<IOrganizationDetailsProps> = ({ data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('adminOrganizationDetails');

  const isActivatingOrDeactivating = useSelector(adminOrganizationActivatingOrDeactivating);

  const initialValues = useMemo(() => convertAdminOrganizationDetailsIntoFormValues(data), [data]);
  const [showActiveConfirmation, openActiveConfirmation, closeActiveConfirmation] = useToggle();

  const handleSubmit = useCallback(
    (values: IAdminOrganizationFormValues) => {
      const payload = convertFormValuesToEditPayload(data.id, values);
      return dispatch(handleAdminEditOrganization(payload));
    },
    [dispatch, data],
  );

  const handleCancel = useCallback(() => {
    dispatch(removeAdminOrganizationDetails());
  }, [dispatch]);

  const handleActivate = useCallback(() => {
    dispatch(handleAdminActivateOrganization(data.id));
  }, [dispatch, data]);

  const handleDeactivate = useCallback(() => {
    dispatch(handleAdminDeactivateOrganization(data.id));
  }, [dispatch, data]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_EDIT_ORGANIZATION_SUCCESS, () => {
      handleToast('success', t('admin.editOrganizationSuccess'));
      dispatch(handleGetAdminOrganizationDetails(data.id));
    });

    eventBus.on(EventType.ADMIN_ACTIVATE_ORGANIZATION_SUCCESS, () => {
      handleToast('success', t('admin.activateOrganizationSuccess'));
      closeActiveConfirmation();
    });

    eventBus.on(EventType.ADMIN_DEACTIVATE_ORGANIZATION_SUCCESS, () => {
      handleToast('success', t('admin.deactivateOrganizationSuccess'));
      closeActiveConfirmation();
    });

    return () =>
      eventBus.off([
        EventType.ADMIN_EDIT_ORGANIZATION_SUCCESS,
        EventType.ADMIN_ACTIVATE_ORGANIZATION_SUCCESS,
        EventType.ADMIN_DEACTIVATE_ORGANIZATION_SUCCESS,
      ]);
  }, [t, eventBus, closeActiveConfirmation]);

  return (
    <>
      <Form
        // @ts-ignore because of custom props
        component={OrganizationDetailsForm}
        initialValues={initialValues}
        isActive={data.isActive}
        isActivatingOrDeactivating={isActivatingOrDeactivating}
        validate={validateForm}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onActivateOrDeactivate={openActiveConfirmation}
      />

      <ConfirmationModal
        open={showActiveConfirmation}
        isLoading={isActivatingOrDeactivating}
        title={
          data.isActive
            ? t('admin.deactivateOrganizationConfirmation')
            : t('admin.activateOrganizationConfirmation')
        }
        rightActionTitle={data.isActive ? t('admin.deactivate') : t('admin.activate')}
        onConfirm={data.isActive ? handleDeactivate : handleActivate}
        onClose={closeActiveConfirmation}
      />
    </>
  );
};

export default OrganizationDetails;
